import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const CUSTOMER_FIELDS = ["name", "customer_code", "contact_person", "mobile", "email", "gstin", "address"];

function CustomersSection() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null); // row object or null
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/customers`);
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load customers");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name || !form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      await api.post(`/customers`, { ...form });
      toast.success("Customer added");
      setOpen(false); setForm({}); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/customers/${confirmDelete.id}`);
      toast.success(`Deleted "${confirmDelete.name}"`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="master-customers">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">{rows.length} customer{rows.length === 1 ? "" : "s"}</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-orange-600 hover:bg-orange-700" size="sm" data-testid="add-customers-button">
              <Plus className="w-4 h-4 mr-2" />Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New Customer</DialogTitle>
              <DialogDescription>Fill in the details below. Name is required.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {CUSTOMER_FIELDS.map((f) => (
                <div key={f}>
                  <Label className="capitalize text-xs">{f.replace(/_/g, " ")}{f === "name" ? " *" : ""}</Label>
                  <Input
                    value={form[f] || ""}
                    onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                    data-testid={`field-${f}`}
                  />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button onClick={save} className="bg-orange-600 hover:bg-orange-700" data-testid="save-master-button">
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {CUSTOMER_FIELDS.map((f) => (
                <TableHead key={f} className="capitalize">{f.replace(/_/g, " ")}</TableHead>
              ))}
              <TableHead className="text-right w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={CUSTOMER_FIELDS.length + 1} className="text-center text-slate-500 py-8">
                  No customers yet. Click <span className="font-medium">Add Customer</span> to create one.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} data-testid={`customer-row-${r.id}`}>
                  {CUSTOMER_FIELDS.map((f) => (
                    <TableCell key={f}>{String(r[f] ?? "—")}</TableCell>
                  ))}
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setConfirmDelete(r)}
                      data-testid={`delete-customer-${r.id}`}
                      aria-label={`Delete ${r.name}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete customer?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{" "}
              <span className="font-semibold">{confirmDelete?.name}</span>
              {confirmDelete?.customer_code ? ` (${confirmDelete.customer_code})` : ""} from
              the master. Existing return requests that reference this customer name will
              not be affected. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-customer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="confirm-delete-customer"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function AdminMastersPage() {
  return (
    <div className="space-y-6" data-testid="admin-masters-page">
      <div>
        <h1 className="heading text-3xl font-bold text-slate-900">Master Data</h1>
        <p className="text-slate-500 mt-1">Manage customer master</p>
      </div>
      <CustomersSection />
    </div>
  );
}
